import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Ticket, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Activity,
  Shield, 
  Server, 
  Bell,
  Zap,
  BarChart3,
  Eye,
  Filter,
  Calendar,
  FileText,
  TrendingUp,
  Download,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import Chart from '../../components/ui/Chart';
import MockDataService from '../../services/MockDataService';
import { userManagementService } from '../../api/userManagementService';
import { ticketService } from '../../api/ticketService';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [reportsData, setReportsData] = useState(null);
  const [viewReport, setViewReport] = useState(null);
  const [isReportViewModalOpen, setIsReportViewModalOpen] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    dateRange: '30d',
    reportType: 'all',
    status: 'all',
    department: 'all',
    agent: 'all'
  });
  const [filteredReportData, setFilteredReportData] = useState(null);

  useEffect(() => {
    loadDashboardData();
    loadReportsData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await MockDataService.getAdminDashboard();
      if (response.success) {
        const dashboardData = response.data;
        
        // Fetch real user count from API
        try {
          const usersResponse = await userManagementService.getUsers({ page: 0, size: 1 });
          let totalUsers = 0;
          
          if (usersResponse?.data) {
            // Check for paginated response structure
            if (usersResponse.data.data && usersResponse.data.data.totalElements !== undefined) {
              totalUsers = usersResponse.data.data.totalElements || 0;
            } else if (usersResponse.data.totalElements !== undefined) {
              totalUsers = usersResponse.data.totalElements || 0;
            } else if (Array.isArray(usersResponse.data)) {
              totalUsers = usersResponse.data.length;
            }
          } else if (Array.isArray(usersResponse)) {
            totalUsers = usersResponse.length;
          }
          
          // Fetch active tickets count from API
          let activeTickets = 0;
          try {
            const ticketsResponse = await ticketService.getAllTickets({ page: 0, size: 1 });
            const ticketsData = ticketsResponse?.data;
            
            if (ticketsData?.success && ticketsData?.data) {
              // Get total count from pagination data
              activeTickets = ticketsData.data.totalElements || 0;
            }
          } catch (ticketsError) {
            console.error('Error loading active tickets count:', ticketsError);
            // Keep the mock data if API fails
            activeTickets = dashboardData.activeTickets || 0;
          }
          
          // Update dashboard data with real counts
          setDashboardData({
            ...dashboardData,
            totalUsers: totalUsers,
            activeTickets: activeTickets
          });
        } catch (userError) {
          console.error('Error loading user count:', userError);
          // Keep the mock data if API fails
          setDashboardData(dashboardData);
        }
      }
    } catch (error) {
      console.error('Error loading admin dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReportsData = async () => {
    try {
      const response = await MockDataService.getReports();
      if (response.success) {
        setReportsData({
          reports: response.data || [],
          recent: (response.data || []).slice(0, 5),
          total: (response.data || []).length,
          completed: (response.data || []).filter(r => r.status === 'completed').length,
          generating: (response.data || []).filter(r => r.status === 'generating').length
        });
      }
    } catch (error) {
      console.error('Error loading reports data:', error);
    }
  };

  const handleViewReport = (report) => {
    setViewReport(report);
    setIsReportViewModalOpen(true);
    // Load initial filtered data
    applyReportFilters(report, reportFilters);
  };

  const applyReportFilters = (report, filters) => {
    if (!report) return;
    
    // Generate mock analytics data based on filters
    const generateAnalyticsData = () => {
      const dateRangeDays = {
        '7d': 7,
        '30d': 30,
        '90d': 90,
        '1y': 365
      }[filters.dateRange] || 30;

      // Ticket trends data
      const ticketTrends = [];
      const days = dateRangeDays <= 7 ? dateRangeDays : 7;
      const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      
      for (let i = 0; i < days; i++) {
        ticketTrends.push({
          name: dayNames[i] || `Day ${i + 1}`,
          value: Math.floor(Math.random() * 100) + 20
        });
      }

      // Status distribution
      const statusDistribution = [
        { name: 'Open', value: Math.floor(Math.random() * 150) + 50 },
        { name: 'In Progress', value: Math.floor(Math.random() * 100) + 30 },
        { name: 'Resolved', value: Math.floor(Math.random() * 200) + 100 },
        { name: 'Closed', value: Math.floor(Math.random() * 180) + 80 }
      ];

      // Agent performance (if agent filter is not 'all')
      const agentPerformance = [
        { name: 'Agent 1', value: Math.floor(Math.random() * 20) + 80 },
        { name: 'Agent 2', value: Math.floor(Math.random() * 20) + 75 },
        { name: 'Agent 3', value: Math.floor(Math.random() * 20) + 85 },
        { name: 'Agent 4', value: Math.floor(Math.random() * 20) + 70 }
      ];

      // SLA compliance
      const slaCompliance = [
        { name: 'Met', value: Math.floor(Math.random() * 20) + 75 },
        { name: 'Warning', value: Math.floor(Math.random() * 15) + 10 },
        { name: 'Breached', value: Math.floor(Math.random() * 10) + 5 }
      ];

      return {
        ticketTrends,
        statusDistribution,
        agentPerformance,
        slaCompliance,
        summary: {
          totalTickets: ticketTrends.reduce((sum, d) => sum + d.value, 0),
          avgResponseTime: `${(Math.random() * 2 + 1).toFixed(1)}h`,
          resolutionRate: `${(Math.random() * 20 + 75).toFixed(1)}%`,
          slaComplianceRate: `${(Math.random() * 15 + 80).toFixed(1)}%`
        }
      };
    };

    const analyticsData = generateAnalyticsData();
    setFilteredReportData(analyticsData);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...reportFilters, [key]: value };
    setReportFilters(newFilters);
    if (viewReport) {
      applyReportFilters(viewReport, newFilters);
    }
  };

  const handleExportReport = (format) => {
    if (!viewReport) return;
    toast.loading(`Preparing ${viewReport.name} (${format.toUpperCase()})...`, { id: `export-${format}` });
    setTimeout(() => {
      toast.success(`${format.toUpperCase()} ready for ${viewReport.name}`, { id: `export-${format}` });
    }, 1500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const recentAlerts = [
    {
      id: 1,
      type: 'success',
      title: 'System backup completed',
      time: '2 hours ago',
      status: 'completed'
    },
    {
      id: 2,
      type: 'warning',
      title: 'High memory usage detected',
      time: '4 hours ago',
      status: 'warning'
    },
    {
      id: 3,
      type: 'success',
      title: 'SLA compliance maintained',
      time: '6 hours ago',
      status: 'completed'
    },
    {
      id: 4,
      type: 'info',
      title: 'New user registration',
      time: '8 hours ago',
      status: 'info'
    }
  ];

  const systemStatus = [
    {
      name: 'Database',
      status: 'online',
      icon: Server,
      color: 'blue'
    },
    {
      name: 'Application Server',
      status: 'online',
      icon: Server,
      color: 'green'
    },
    {
      name: 'Notification Service',
      status: 'online',
      icon: Bell,
      color: 'yellow'
    },
    {
      name: 'Workflow Engine',
      status: 'online',
      icon: Zap,
      color: 'purple'
    }
  ];

  return (
    <div className="w-full" style={{ backgroundColor: '#F5F7FA', margin: 0, padding: 0 }}>
      {/* Hero Section with Background */}
      <section className="relative py-4 sm:py-5 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ margin: 0 }}>
        {/* Muted background overlay */}
        <div className="absolute inset-0" style={{ backgroundColor: '#F8F9FA' }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center space-y-2 sm:space-y-3">
            <div className="inline-block px-2 sm:px-3 py-1 sm:py-1.5 rounded-full mb-1 sm:mb-2" style={{ backgroundColor: 'rgba(72, 61, 133, 0.1)', border: '1px solid rgba(72, 61, 133, 0.2)' }}>
              <span className="text-xs font-semibold" style={{ color: '#483D85' }}>System Administration & Management</span>
            </div>
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-tight" style={{ color: '#252525', margin: 0 }}>
              Admin Dashboard
            </h1>
            <p className="text-sm sm:text-base max-w-2xl mx-auto text-center" style={{ color: '#585858', margin: '0 auto', padding: 0 }}>
              Monitor system performance, manage users, and oversee operations.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <div className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8 relative" style={{ backgroundColor: '#F5F7FA', margin: 0, paddingTop: '1.5rem' }}>
        <div className="space-y-6">

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate('/admin/users/all')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Total Users</p>
                    <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{dashboardData.totalUsers || 0}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate('/admin/tickets')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Active Tickets</p>
                    <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{dashboardData.activeTickets || 0}</p>
                  </div>
                  <Ticket className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">System Health</p>
                    <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{dashboardData.systemHealth || '98%'}</p>
                  </div>
                  <Shield className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Response Time</p>
                    <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">{dashboardData.avgResponseTime || '2.4h'}</p>
                  </div>
                  <Clock className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* System Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Server className="h-5 w-5" />
                  <span>System Status</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {systemStatus.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.name} className="border border-border-light dark:border-border-dark rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 text-${item.color}-600`} />
                            <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark">
                              {item.name}
                            </h3>
                          </div>
                          <Badge className={item.status === 'online' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'}>
                            {item.status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Recent Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <AlertTriangle className="h-5 w-5" />
                  <span>Recent Alerts</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentAlerts.map((alert) => (
                    <div key={alert.id} className="border border-border-light dark:border-border-dark rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-start gap-2">
                          {alert.type === 'success' && <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />}
                          {alert.type === 'warning' && <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />}
                          {alert.type === 'info' && <Activity className="h-4 w-4 text-blue-600 mt-0.5" />}
                          <div>
                            <h3 className="font-medium text-text-primary-light dark:text-text-primary-dark">
                              {alert.title}
                            </h3>
                            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
                              {alert.time}
                            </p>
                          </div>
                        </div>
                        <Badge className={alert.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : alert.status === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'}>
                          {alert.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Reports Overview */}
          {/* <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5" />
                <span>Reports Overview</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 border border-border-light dark:border-border-dark rounded-lg">
                  <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                    {reportsData?.total || 0}
                  </div>
                  <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">Total Reports</div>
                </div>
                <div className="text-center p-4 border border-border-light dark:border-border-dark rounded-lg">
                  <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                    {reportsData?.completed || 0}
                  </div>
                  <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">Completed</div>
                </div>
                <div className="text-center p-4 border border-border-light dark:border-border-dark rounded-lg">
                  <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                    {reportsData?.generating || 0}
                  </div>
                  <div className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">Generating</div>
                </div>
              </div>
              <div className="space-y-4">
                {reportsData?.recent && reportsData.recent.length > 0 ? (
                  <div className="space-y-3">
                    {reportsData.recent.map((report) => (
                      <div key={report.id} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <div className="flex items-center gap-4 flex-1">
                          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                            <BarChart3 className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 dark:text-white">{report.name || 'Untitled Report'}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{report.description || 'No description'}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={report.status === 'completed' ? 'success' : report.status === 'generating' ? 'info' : 'destructive'} size="sm">
                                {report.status || 'unknown'}
                              </Badge>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {report.createdAt ? new Date(report.createdAt).toLocaleDateString() : 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewReport(report)}
                            title="View Report with Analytics"
                            aria-label={`View ${report.name || 'report'}`}
                            className="border-[#006600] text-[#006600] hover:bg-green-50 dark:hover:bg-green-900/20"
                          >
                            <Eye className="h-4 w-4" />
                            <span className="hidden sm:inline ml-1">View</span>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">No recent reports available.</p>
                  </div>
                )}
                <div className="flex justify-end">
                  <Button 
                    onClick={() => navigate('/admin/reports')}
                    className="border-[#006600] text-[#ffffff] hover:bg-green-50"
                  >
                    View All Reports
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card> */}
        </div>
      </div>

      {/* Report View Modal with Analytics & Filters */}
      <Modal
        isOpen={isReportViewModalOpen}
        onClose={() => {
          setIsReportViewModalOpen(false);
          setViewReport(null);
          setFilteredReportData(null);
        }}
        title={viewReport?.name || 'Report Analytics'}
        size="full"
      >
        {viewReport && (
          <div className="space-y-6">
            {/* Report Header & Info */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {viewReport.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {viewReport.description || 'No description available'}
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <span>Status: <strong className="capitalize text-gray-700 dark:text-gray-300">{viewReport.status}</strong></span>
                    <span>Type: <strong className="text-gray-700 dark:text-gray-300">{viewReport.type || 'General'}</strong></span>
                    <span>Created: <strong className="text-gray-700 dark:text-gray-300">
                      {viewReport.createdAt ? new Date(viewReport.createdAt).toLocaleString() : 'N/A'}
                    </strong></span>
                    <span>Size: <strong className="text-gray-700 dark:text-gray-300">{viewReport.size || 'N/A'} KB</strong></span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportReport('pdf')}
                    disabled={viewReport.status !== 'completed'}
                    className="flex items-center gap-2 border-[#006600] text-[#006600] hover:bg-green-50 disabled:border-gray-300 disabled:text-gray-400"
                  >
                    <Download className="h-4 w-4" />
                    PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportReport('csv')}
                    disabled={viewReport.status !== 'completed'}
                    className="flex items-center gap-2 border-[#006600] text-[#006600] hover:bg-green-50 disabled:border-gray-300 disabled:text-gray-400"
                  >
                    <Download className="h-4 w-4" />
                    CSV
                  </Button>
                </div>
              </div>
            </div>

            {/* Filter Section */}
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                <h4 className="font-semibold text-gray-900 dark:text-white">Analytics Filters</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <Label htmlFor="filter-date-range" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                    Date Range
                  </Label>
                  <Select
                    value={reportFilters.dateRange}
                    onValueChange={(value) => handleFilterChange('dateRange', value)}
                  >
                    <SelectTrigger id="filter-date-range" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7d">Last 7 Days</SelectItem>
                      <SelectItem value="30d">Last 30 Days</SelectItem>
                      <SelectItem value="90d">Last 90 Days</SelectItem>
                      <SelectItem value="1y">Last Year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="filter-report-type" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                    Report Type
                  </Label>
                  <Select
                    value={reportFilters.reportType}
                    onValueChange={(value) => handleFilterChange('reportType', value)}
                  >
                    <SelectTrigger id="filter-report-type" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="tickets">Tickets</SelectItem>
                      <SelectItem value="agents">Agents</SelectItem>
                      <SelectItem value="performance">Performance</SelectItem>
                      <SelectItem value="sla">SLA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="filter-status" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                    Status
                  </Label>
                  <Select
                    value={reportFilters.status}
                    onValueChange={(value) => handleFilterChange('status', value)}
                  >
                    <SelectTrigger id="filter-status" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in-progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="filter-department" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                    Department
                  </Label>
                  <Select
                    value={reportFilters.department}
                    onValueChange={(value) => handleFilterChange('department', value)}
                  >
                    <SelectTrigger id="filter-department" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Departments</SelectItem>
                      <SelectItem value="support">Support</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="sales">Sales</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="filter-agent" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                    Agent
                  </Label>
                  <Select
                    value={reportFilters.agent}
                    onValueChange={(value) => handleFilterChange('agent', value)}
                  >
                    <SelectTrigger id="filter-agent" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Agents</SelectItem>
                      <SelectItem value="agent1">Agent 1</SelectItem>
                      <SelectItem value="agent2">Agent 2</SelectItem>
                      <SelectItem value="agent3">Agent 3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Analytics Summary Cards */}
            {filteredReportData?.summary && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Total Tickets</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {filteredReportData.summary.totalTickets}
                        </p>
                      </div>
                      <Ticket className="h-8 w-8 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Avg Response Time</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {filteredReportData.summary.avgResponseTime}
                        </p>
                      </div>
                      <Clock className="h-8 w-8 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Resolution Rate</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {filteredReportData.summary.resolutionRate}
                        </p>
                      </div>
                      <CheckCircle className="h-8 w-8 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">SLA Compliance</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {filteredReportData.summary.slaComplianceRate}
                        </p>
                      </div>
                      <Shield className="h-8 w-8 text-yellow-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
{/* Analytics Charts */}
            {filteredReportData && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Ticket Trends
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Chart type="line" data={filteredReportData.ticketTrends} height={300} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Status Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Chart type="pie" data={filteredReportData.statusDistribution} height={300} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Agent Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Chart type="bar" data={filteredReportData.agentPerformance} height={300} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      SLA Compliance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Chart type="pie" data={filteredReportData.slaCompliance} height={300} />
                  </CardContent>
                </Card>
              </div>
            )}

                    {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={() => {
                  setIsReportViewModalOpen(false);
                  setViewReport(null);
                  setFilteredReportData(null);
                }}
                className="border-[#006600] text-[#006600] hover:bg-green-50"
              >
                Close
              </Button>
              <Button
                onClick={() => navigate('/admin/reports')}
                className="bg-[#006600] hover:bg-[#005500] text-white border-[#006600]"
              >
                View Full Reports Page
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminDashboard;
